import { useCallback, useEffect } from 'react';
import { authStore } from '@/stores';
import * as amplitude from '@amplitude/analytics-browser';
import { AnalyticEvent, AnalyticsEvents, UserDto } from '@sweatpals/shared';

/**
 * Custom hook for integrating Amplitude Analytics into React applications.
 * Automatically initializes Amplitude SDK on mount if API key is provided.
 *
 * @example
 * ```tsx
 * const { logEventTyped } = useAmplitudeAnalytics();
 *
 * // Log a typed analytics event
 * logEventTyped('USER_SIGNED_UP', {
 *   method: 'email',
 *   timestamp: new Date()
 * });
 * ```
 *
 * @remarks
 * - Automatically initializes Amplitude when component mounts
 * - Sets user ID and properties when current user changes
 * - Provides type-safe event logging through `logEventTyped` method
 * - Requires environment variables:
 *   - NEXT_PUBLIC_AMPLITUDE_API_KEY: Amplitude API key for initialization
 *   - NEXT_PUBLIC_SENTRY_RELEASE: App version to be sent with analytics
 *
 * @returns Object containing the logEventTyped method for tracking events
 */
export const useAmplitudeAnalytics = () => {
	const currentUser = authStore.user;

	useEffect(() => {
		if (typeof window !== undefined) {
			amplitude.init(process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY, {
				appVersion: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
				autocapture: true,
			});
		}
	}, []);

	const setCurrentUserId = useCallback((currentUser: UserDto) => {
		try {
			amplitude.setUserId(currentUser.id);

			const identify = new amplitude.Identify();

			for (const [key, value] of Object.entries(currentUser)) {
				if (key !== 'id') {
					identify.set(key, value);
				}
			}

			amplitude.identify(identify);
			console.info('[Amplitude] User id set: ', currentUser.id);
		} catch (e) {
			console.error('[Amplitude] Error while setting user id', e);
		}
	}, []);

	useEffect(() => {
		if (currentUser?.id) {
			setCurrentUserId(currentUser);
		}
	}, [setCurrentUserId, currentUser?.id]);

	const logEventTyped = useCallback(<T extends AnalyticsEvents>(event: T, metadata: AnalyticEvent<T>, amplitudeOptions?: amplitude.Types.EventOptions) => {
		try {
			metadata = {
				...metadata,
				data_stream: 'landing'
			}

			amplitude.track(event, metadata, amplitudeOptions);
			console.info('[Amplitude] Event logged: ', { event, metadata });
		} catch (e) {
			console.error('[Amplitude] Error while logging analytical event', e);
		}
	}, []);

	const setUserIdExternally = useCallback((userId: string) => {
		try {
			amplitude.setUserId(userId);
			console.info('[Amplitude] User id set: ', userId);
		} catch (e) {
			console.error('[Amplitude] Error while setting user id', e);
		}
	}, []);

	return {
		logEventTyped,
		setUserIdExternally,
	};
}
